import React, { useState } from 'react';
import axios from 'axios';
import { Mail, MapPin, Phone, Send, Facebook, MessageCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import useReveal from '../hooks/useReveal';
import { CONTACT, IMAGES } from '../data/content';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: '', email: '', phone: '', subject: '', message: '' };

export default function Contact() {
  const { t, lang } = useLang();
  const cn = lang === 'zh' ? 'font-cn' : '';
  useReveal([lang]);

  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState('idle'); // idle | sending | success | error

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm(initial);
      setTimeout(() => setStatus('idle'), 6000);
    } catch {
      setStatus('error');
    }
  };

  const inputCls =
    'w-full bg-[#FAF8F3] border border-[#e2d7c1] rounded-sm px-4 py-3 text-[#0B1F3A] placeholder-[#9aa1ab] focus:outline-none focus:border-[#C6A15B] focus:ring-1 focus:ring-[#C6A15B] transition-colors';

  return (
    <div>
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.consultation} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 grain" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <p className={`text-[#C6A15B] tracking-[0.3em] uppercase text-xs mb-4 animate-fadeUp ${cn}`}>{t('contact.eyebrow')}</p>
          <h1 className={`font-serif-display text-4xl md:text-5xl font-semibold text-white animate-fadeUp text-balance ${cn}`} style={{ animationDelay: '0.15s' }}>{t('contact.title')}</h1>
          <p className={`text-white/75 mt-4 animate-fadeUp ${cn}`} style={{ animationDelay: '0.3s' }}>{t('contact.subtitle')}</p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-5 gap-12">
          {/* Info */}
          <div className="lg:col-span-2 reveal reveal-left">
            <h2 className={`font-serif-display text-2xl font-semibold text-[#0B1F3A] mb-2 ${cn}`}>{t('contact.emailUs')}</h2>
            <a href={`mailto:${CONTACT.email}`} className="text-[#A9853E] text-xl font-medium link-underline">{CONTACT.email}</a>

            <div className="gold-rule my-8" />

            <ul className="space-y-6">
              <li className="flex items-start gap-4">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[#FAF8F3] text-[#A9853E] shrink-0"><MapPin className="w-5 h-5" /></span>
                <div>
                  <p className={`text-xs uppercase tracking-widest text-[#9aa1ab] mb-1 ${cn}`}>{t('contact.address')}</p>
                  <p className="text-[#0B1F3A] font-medium">{CONTACT.addressLine1}<br />{CONTACT.addressLine2}</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[#FAF8F3] text-[#A9853E] shrink-0"><Phone className="w-5 h-5" /></span>
                <div>
                  <p className={`text-xs uppercase tracking-widest text-[#9aa1ab] mb-1 ${cn}`}>{t('contact.phone')}</p>
                  <a href={CONTACT.whatsappLink} className="text-[#0B1F3A] font-medium hover:text-[#A9853E] transition-colors">{CONTACT.whatsapp}</a>
                </div>
              </li>
            </ul>

            <p className={`text-sm text-[#5c6470] mt-8 mb-4 ${cn}`}>{t('contact.findUs')}</p>
            <div className="flex gap-3">
              <a href={CONTACT.facebook} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full border border-[#e2d7c1] flex items-center justify-center text-[#0B1F3A] hover:bg-[#C6A15B] hover:border-[#C6A15B] transition-all"><Facebook className="w-5 h-5" /></a>
              <a href={CONTACT.whatsappLink} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full border border-[#e2d7c1] flex items-center justify-center text-[#0B1F3A] hover:bg-[#C6A15B] hover:border-[#C6A15B] transition-all"><MessageCircle className="w-5 h-5" /></a>
              <a href={CONTACT.wechat} target="_blank" rel="noreferrer" className="w-11 h-11 rounded-full border border-[#e2d7c1] flex items-center justify-center text-[#0B1F3A] text-xs font-semibold hover:bg-[#C6A15B] hover:border-[#C6A15B] transition-all">微信</a>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3 reveal reveal-right">
            <form onSubmit={onSubmit} className="bg-[#FAF8F3] border border-[#eadfca] rounded-sm p-8 md:p-10">
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className={`block text-sm text-[#0B1F3A] mb-2 ${cn}`}>{t('contact.formName')}</label>
                  <input name="name" required value={form.name} onChange={onChange} className={inputCls} />
                </div>
                <div>
                  <label className={`block text-sm text-[#0B1F3A] mb-2 ${cn}`}>{t('contact.formEmail')}</label>
                  <input type="email" name="email" required value={form.email} onChange={onChange} className={inputCls} />
                </div>
                <div>
                  <label className={`block text-sm text-[#0B1F3A] mb-2 ${cn}`}>{t('contact.formPhone')}</label>
                  <input name="phone" value={form.phone} onChange={onChange} className={inputCls} />
                </div>
                <div>
                  <label className={`block text-sm text-[#0B1F3A] mb-2 ${cn}`}>{t('contact.formSubject')}</label>
                  <input name="subject" value={form.subject} onChange={onChange} className={inputCls} />
                </div>
              </div>
              <div className="mt-5">
                <label className={`block text-sm text-[#0B1F3A] mb-2 ${cn}`}>{t('contact.formMessage')}</label>
                <textarea name="message" required rows={5} value={form.message} onChange={onChange} className={inputCls} />
              </div>

              {status === 'success' && (
                <div className={`mt-5 flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-sm px-4 py-3 text-sm ${cn}`}>
                  <CheckCircle2 className="w-5 h-5" /> {t('contact.formSuccess')}
                </div>
              )}
              {status === 'error' && (
                <div className={`mt-5 text-red-700 bg-red-50 border border-red-200 rounded-sm px-4 py-3 text-sm ${cn}`}>{t('contact.formError')}</div>
              )}

              <button type="submit" disabled={status === 'sending'} className={`mt-6 inline-flex items-center gap-2 gold-btn rounded-full px-8 py-4 font-semibold disabled:opacity-70 ${cn}`}>
                {status === 'sending' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                {status === 'sending' ? t('contact.formSending') : t('contact.formSubmit')}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="h-[420px] w-full">
        <iframe title="map" src="https://www.google.com/maps?q=Calle+De+Valde%C3%B3n+10,+28947+Fuenlabrada,+Madrid,+Espa%C3%B1a&output=embed" width="100%" height="100%" style={{ border: 0 }} loading="lazy" />
      </section>
    </div>
  );
}
